/**
 * AuditPage - Event Log
 *
 * Industrial control room aesthetic with a data-dense event log table.
 * Features action-based filtering and status indicators with glow effects.
 */

import { useState } from 'react';
import {
  Badge,
  Box,
  Card,
  Group,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, buildQueryString } from '@commander/api-client';

type ActionFilter = 'all' | 'stun' | 'unstun' | 'checkout' | 'checkin' | 'login';

interface AuditEntry {
  id: string;
  action: string;
  targetType: string;
  targetId: string;
  actorId: string;
  actorUsername: string;
  result: 'success' | 'failure';
  errorMessage: string | null;
  timestamp: string;
  metadata: Record<string, unknown>;
}

interface AuditResponse {
  items: AuditEntry[];
  total: number;
}

function useAuditLog(action: ActionFilter) {
  const filters: Record<string, string> = {};
  if (action !== 'all') {
    filters.action = action;
  }

  return useQuery({
    queryKey: ['audit', action],
    queryFn: () => apiFetch<AuditResponse>(`/api/audit${buildQueryString(filters)}`),
    staleTime: 30_000,
  });
}

/** Action badge with colour coding */
const actionConfig: Record<
  string,
  { colour: string; glow: string; label: string }
> = {
  stun: {
    colour: '#da1e28',
    glow: 'rgba(218, 30, 40, 0.4)',
    label: 'STUN',
  },
  unstun: {
    colour: '#24a148',
    glow: 'rgba(36, 161, 72, 0.4)',
    label: 'UNSTUN',
  },
  checkout: {
    colour: '#0f62fe',
    glow: 'rgba(15, 98, 254, 0.4)',
    label: 'CHECKOUT',
  },
  checkin: {
    colour: '#8a3ffc',
    glow: 'rgba(138, 63, 252, 0.4)',
    label: 'CHECKIN',
  },
  login: {
    colour: '#525252',
    glow: 'none',
    label: 'LOGIN',
  },
};

function ActionBadge({ action }: { action: string }) {
  const config = actionConfig[action] ?? {
    colour: '#525252',
    glow: 'none',
    label: action.toUpperCase(),
  };

  return (
    <Badge
      size="sm"
      variant="filled"
      styles={{
        root: {
          backgroundColor: config.colour,
          boxShadow: config.glow !== 'none' ? `0 0 8px ${config.glow}` : undefined,
          textTransform: 'uppercase',
          fontWeight: 700,
          letterSpacing: '0.05em',
          fontSize: 10,
        },
      }}
    >
      {config.label}
    </Badge>
  );
}

function ResultBadge({ result }: { result: 'success' | 'failure' }) {
  const isSuccess = result === 'success';

  return (
    <Box
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: isSuccess ? '#24a148' : '#da1e28',
        boxShadow: isSuccess
          ? '0 0 8px rgba(36, 161, 72, 0.6)'
          : '0 0 8px rgba(218, 30, 40, 0.6)',
      }}
    />
  );
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export function AuditPage() {
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');
  const { data, isLoading } = useAuditLog(actionFilter);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Box>
          <Title order={2}>Audit Log</Title>
          <Text c="dimmed" size="sm">
            System events and user actions
          </Text>
        </Box>

        <SegmentedControl
          value={actionFilter}
          onChange={(value) => setActionFilter(value as ActionFilter)}
          data={[
            { label: 'All', value: 'all' },
            { label: 'Stun', value: 'stun' },
            { label: 'Unstun', value: 'unstun' },
            { label: 'Checkout', value: 'checkout' },
            { label: 'Checkin', value: 'checkin' },
          ]}
          styles={{
            root: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)' },
            label: { color: 'var(--text-secondary)' },
            indicator: { backgroundColor: 'var(--blue-bg)', boxShadow: 'none' },
            control: { border: 'none' },
          }}
        />
      </Group>

      <Card withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 40 }}>Status</Table.Th>
              <Table.Th style={{ width: 100 }}>Action</Table.Th>
              <Table.Th>Target</Table.Th>
              <Table.Th>Actor</Table.Th>
              <Table.Th>Details</Table.Th>
              <Table.Th style={{ width: 180 }}>Timestamp</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" ta="center">
                    Loading...
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : data?.items.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" ta="center">
                    No audit entries found.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              data?.items.map((entry) => (
                <Table.Tr
                  key={entry.id}
                  style={{
                    backgroundColor:
                      entry.result === 'failure' ? 'rgba(218, 30, 40, 0.05)' : undefined,
                  }}
                >
                  <Table.Td>
                    <ResultBadge result={entry.result} />
                  </Table.Td>
                  <Table.Td>
                    <ActionBadge action={entry.action} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" tt="uppercase" mb={2}>
                      {entry.targetType}
                    </Text>
                    <Text size="sm" ff="monospace" fw={600}>
                      {entry.targetId}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {entry.actorUsername}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {entry.errorMessage ? (
                      <Text size="sm" c="red">
                        {entry.errorMessage}
                      </Text>
                    ) : entry.metadata && Object.keys(entry.metadata).length > 0 ? (
                      <Text size="sm" c="dimmed">
                        {Object.entries(entry.metadata)
                          .map(([key, value]) => `${key}: ${value}`)
                          .join(', ')}
                      </Text>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {formatTimestamp(entry.timestamp)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatTimeAgo(entry.timestamp)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
